import { Form, FormInstance } from 'antd';
import { CKEditor } from 'ckeditor4-react';
import React, { memo, useEffect, useRef } from 'react';
import { ANIMATION_DURATION, languages, ruLangLabels } from '../../admin-knowledge-base.constants';
import { KBEntity } from '../../admin-knowledge-base.type';
import { Languages } from '/react4xp/parts/knowledge-base-common/knowledge-base-common.type';

interface ContentFieldProps {
    activeLanguage: Languages;
    form: FormInstance<KBEntity>;
    id: string;
    minLength?: number;
}

const CONTENT = 'content';
const DRAFT = 'draft';

const plainTextLength = (html: string | undefined | null): number => {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    // убираем zero-width и неразрывные пробелы
    const text = (div.textContent || div.innerText || '')
        .replace(/\u00A0/g, ' ')
        .replace(/\u200B/g, '')
        .trim();
    return text.length;
};

const ContentField = memo(({ activeLanguage, form, id, minLength = 0 }: ContentFieldProps) => {
    const editorRef = useRef<any>(null);

    useEffect(() => {
        const editor = editorRef?.current;
        if (editor) {
            const content = form.getFieldValue([activeLanguage, CONTENT]) || '';
            setTimeout(() => {
                editor.setData(content);
            }, ANIMATION_DURATION / 3);
        }
    }, [id, activeLanguage]);

    const draft = Form.useWatch([DRAFT, CONTENT], form);

    useEffect(() => {
        form.setFieldValue([activeLanguage, CONTENT], draft);
    }, [draft]);

    return (
        <>
            {languages.map((language) => (
                <Form.Item
                    hidden
                    key={language}
                    name={[language, CONTENT]}
                />
            ))}
            <Form.Item
                label="Текст статьи"
                labelCol={{ span: 24 }}
                name={[DRAFT, CONTENT]}
                valuePropName="data"
                getValueFromEvent={({ editor }) => editor.getData()}
                required
                rules={[
                    {
                        validator: () => {
                            const missingField = languages.find(
                                (language) => !form.getFieldValue([language, CONTENT])
                            );
                            if (missingField) {
                                return Promise.reject(
                                    `Не заполнен текст статьи (${ruLangLabels[missingField]})`
                                );
                            }

                            if (minLength > 0) {
                                const belowMin = languages.find((language) => {
                                    const html = form.getFieldValue([language, CONTENT]);
                                    return plainTextLength(html) < minLength;
                                });
                                if (belowMin) {
                                    return Promise.reject(
                                        `Текст статьи короче ${minLength} символов (${ruLangLabels[belowMin]})`
                                    );
                                }
                            }
                            return Promise.resolve();
                        },
                    },
                ]}
            >
                <CKEditor
                    editorUrl="/_/asset/isnareactcms/ckeditor/ckeditor.js"
                    onInstanceReady={({ editor }) => {
                        editorRef.current = editor;
                    }}
                    config={{
                        toolbar: [
                            ['Bold', 'Italic', 'Underline'],
                            ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'],
                            ['NumberedList', 'BulletedList'],
                            ['Image'],
                            ['Youtube'],
                            ['Link'],
                            ['Undo', 'Redo'],
                        ],
                        extraPlugins: ['justify', 'youtube'],
                        youtube_disabled_fields: ['txtUrl'],
                    }}
                />
            </Form.Item>
        </>
    );
});

export default ContentField;
